import React, { useMemo } from 'react';

import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import EmptyState from '~/components/core/EmptyState';
import Text from '~/components/core/TextComponents/Text';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import type { FundAccountRuleInfo } from '~/server_shared/openapi/data-contracts';
import { subOrgIdToNameDict } from '~/Utils';

import type { SysconfigOrganization } from '../types';

import FundAccountRuleRow from './FundAccountRuleRow';
import FundAccountRulesTableHeader from './FundAccountRulesTableHeader';

/** Why dragging goes inert under a filter: the reorder endpoint takes the whole target order and
 *  rejects anything that is not an exact permutation of every live rule in the organization, so a
 *  drag inside a filtered subset has no valid body — and priority only means anything across the
 *  full set anyway. */
const REORDER_DISABLED_NOTE = 'Clear the filters to change rule priority.';

interface FundAccountRulesTableProps {
  organization: SysconfigOrganization;
  rules: FundAccountRuleInfo[];
  isProcessing: boolean;
  isReorderDisabled: boolean;
  emptyStateText: string;
  onReorder: (reorder: DndOrderedListReorder) => void;
  onEditRule: (rule: FundAccountRuleInfo) => void;
  onDeleteRule: (rule: FundAccountRuleInfo) => void | Promise<void>;
  onEnabledChanged: (rule: FundAccountRuleInfo) => void | Promise<void>;
}

const FundAccountRulesTable: React.FC<FundAccountRulesTableProps> = ({
  organization,
  rules,
  isProcessing,
  isReorderDisabled,
  emptyStateText,
  onReorder,
  onEditRule,
  onDeleteRule,
  onEnabledChanged,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  // Identical for every row — build once per `organization`, not per cell render.
  const subOrgIdToName = useMemo(() => subOrgIdToNameDict(organization), [organization]);
  const showSubOrgColumn = !!organization.sub_organizations_enabled;
  const isDragDisabled = isReorderDisabled || isProcessing;

  // The note ships with the empty state too: a filter that matches nothing is exactly when the
  // user is most likely to wonder why the handles went inert, so it is the worst case to omit the
  // explanation from.
  const reorderNote = isReorderDisabled ? (
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="pt-8">
      {REORDER_DISABLED_NOTE}
    </Text>
  ) : null;

  if (rules.length === 0) {
    return (
      <div>
        <EmptyState subtitle={emptyStateText} />
        {reorderNote}
      </div>
    );
  }

  return (
    <div>
      <FundAccountRulesTableHeader showSubOrgColumn={showSubOrgColumn} />
      <DndOrderedList
        items={rules}
        getItemId={(rule) => String(rule.id)}
        disabled={isDragDisabled}
        showDropLine
        onReorder={onReorder}
        renderItem={(rule, state) => (
          <FundAccountRuleRow
            rule={rule}
            dragHandleProps={state.dragHandleProps}
            isDragDisabled={isDragDisabled}
            isProcessing={isProcessing}
            showSubOrgColumn={showSubOrgColumn}
            subOrgIdToName={subOrgIdToName}
            lobConfigurationsDict={lobConfigurationsDict}
            onEdit={onEditRule}
            onDelete={onDeleteRule}
            onEnabledChanged={onEnabledChanged}
          />
        )}
      />
      {reorderNote}
    </div>
  );
};

export default FundAccountRulesTable;
